import {IonAlert} from '@ionic/react';
import classnames from 'classnames';
import React, {useContext, useState} from 'react';
import {useFormikContext} from 'formik';
import AreYouSure from 'src/components/AreYouSure';
import Button from 'src/components/button';
import {useMediaQuery} from 'src/hooks/useMediaQuery';
import {PlatformContext} from '../PlatformContext';
import DeleteButton from './DeleteButton';
import {NativeAlertDeleteButton} from './NativeAlertDeleteButton';

interface Props {
  _id: string;
  onCancel: () => void;
  onDelete: () => void;
  onAddAnother?: () => void;
  formName: string;
  nativeAlertForDelete?: boolean;
  hideDelete?: boolean;
}

const PortfolioFormFooter = ({
  _id,
  onCancel,
  onDelete,
  onAddAnother,
  formName,
  nativeAlertForDelete,
  hideDelete,
}: Props) => {
  const [showAlert, setShowAlert] = useState(false);
  const formik = useFormikContext<{new?: boolean}>();

  const mq = useMediaQuery();
  const disableSave = !formik.isValid || formik.isSubmitting;
  const platformContext = useContext(PlatformContext);

  return (
    <div
      className={classnames('portfolio-form-footer', 'prefix-flex', 'prefix-align-items', {
        'prefix-flex-direction-column': mq.MS_AND_DOWN,
      })}
    >
      <div
        className={classnames(
          'portfolio-form-footer-cancel-delete',
          'prefix-flex',
          'prefix-flex prefix-flex-grow-2',
          {'prefix-align-items': !mq.MS_AND_DOWN}
        )}
      >
        {nativeAlertForDelete ? (
          <NativeAlertDeleteButton setShowAlert={setShowAlert} formName={formName} />
        ) : (
          <DeleteButton _id={_id} formName={formName} onDelete={onDelete} hideDelete={hideDelete} />
        )}

        {platformContext.isIonic && (
          <IonAlert
            isOpen={showAlert}
            header="Are you sure you want to remove this course?"
            message="This may impact your earnings from the colleges you're following."
            buttons={[
              {
                text: 'Go Back',
                role: 'cancel',
                handler: () => setShowAlert(false),
              },
              {
                text: 'Confirm',
                cssClass: 'delete',
                handler: onDelete,
              },
            ]}
          />
        )}

        <div
          className={classnames('portfolio-form-footer-cancel-button', {
            'is-new': formik.values.new,
          })}
        >
          <AreYouSure
            id={`cancel-${_id}`}
            text="Are you sure you want to cancel editing this item?"
            buttonText="Yes, Cancel"
            shouldPrompt={formik.dirty}
            disabled={formik.isSubmitting}
            onYes={onCancel}
            buttonName={`${formName} form-footer`}
          >
            <Button
              color="primary"
              outline
              width={mq.MS_AND_DOWN ? '100%' : '80px'}
              margin={false}
              buttonName={`${formName} form-footer are-you-sure cancel`}
            >
              Cancel
            </Button>
          </AreYouSure>
        </div>
      </div>

      <div
        className={classnames('portfolio-form-footer-save-button', {
          'add-another': onAddAnother,
        })}
      >
        <Button
          color="primary"
          type="submit"
          disabled={disableSave}
          width={mq.MS_AND_DOWN ? '100%' : '80px'}
          margin={false}
          buttonName={`${formName} form-footer save`}
        >
          {formik.isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {formik.values.new && onAddAnother && (
        <div className="portfolio-form-footer-save-button add-another">
          <Button
            color="primary"
            onClick={async () => {
              await formik.submitForm();
              onAddAnother();
            }}
            disabled={disableSave}
            width={mq.MS_AND_DOWN ? '100%' : '170px'}
            margin={false}
            buttonName={`${formName} form-footer save-and-add-another`}
          >
            {formik.isSubmitting ? 'Saving...' : 'Save + Add Another'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PortfolioFormFooter;
